/**
 * Contextual internal-link graph.
 *
 * Connects the three content clusters — blog guides, service pages, and
 * Denver suburb landing pages — using descriptive, keyword-bearing anchor
 * text (never "click here" / "read more").
 */
import { serviceAreas } from "@/data/serviceAreas";

export type TopicKey =
  | "home-theater"
  | "dolby-atmos"
  | "control4"
  | "smart-home"
  | "whole-home-audio"
  | "lighting"
  | "shading"
  | "outdoor"
  | "networking"
  | "prewire"
  | "tv-mounting"
  | "av-installation"
  | "whole-home-video"
  | "commercial"
  | "security";

export type LinkItem = {
  to: string;
  /** Descriptive anchor text. */
  anchor: string;
  blurb: string;
};

/** Service pages, keyed by topic. */
export const serviceLinks: Record<TopicKey, LinkItem> = {
  "home-theater": {
    to: "/services/home-theater",
    anchor: "custom home theater design and installation in Denver",
    blurb: "Dedicated Atmos theaters and media rooms, engineered acoustically end to end.",
  },
  "dolby-atmos": {
    to: "/services/dolby-atmos-home-theater",
    anchor: "Dolby Atmos home theater installation in Denver",
    blurb: "Immersive speaker layouts, overhead channels, bass integration, and room calibration.",
  },
  control4: {
    to: "/services/control4",
    anchor: "Control4 dealer and installer in Denver",
    blurb: "Certified Control4 design, installation, programming, and takeover support.",
  },
  "smart-home": {
    to: "/services/smart-home",
    anchor: "smart home automation and integrated control",
    blurb: "One app and one keypad layout for lights, audio, shades, climate, and cameras.",
  },
  "whole-home-audio": {
    to: "/services/whole-home-audio",
    anchor: "whole-home audio with in-wall and in-ceiling speakers",
    blurb: "Multi-room music zones wired for even coverage instead of loud spots.",
  },
  lighting: {
    to: "/services/lighting",
    anchor: "architectural lighting control and keypad design",
    blurb: "Layered lighting scenes, tunable white, and keypads that make sense.",
  },
  shading: {
    to: "/services/shading",
    anchor: "motorized shades and window treatment automation",
    blurb: "Automated shading that manages Colorado's high-altitude sun and glare.",
  },
  outdoor: {
    to: "/services/outdoor",
    anchor: "outdoor TV and landscape audio installation",
    blurb: "Weather-rated displays and buried landscape speakers built for Front Range patios.",
  },
  networking: {
    to: "/services/networking",
    anchor: "enterprise-grade home Wi-Fi and networking",
    blurb: "Wired backbones and managed access points that keep streaming and control stable.",
  },
  prewire: {
    to: "/services/prewire",
    anchor: "new construction low-voltage prewire",
    blurb: "Structured wiring designed before drywall so nothing has to be retrofitted later.",
  },
  "tv-mounting": {
    to: "/services/tv-mounting",
    anchor: "professional TV mounting with in-wall power",
    blurb: "Stud-anchored mounts, recessed outlets, and fully concealed cabling.",
  },
  "av-installation": {
    to: "/services/av-installation",
    anchor: "AV installation in Denver",
    blurb: "Displays, distributed audio, racks, and control programming installed end to end.",
  },
  "whole-home-video": {
    to: "/services/whole-home-video",
    anchor: "whole-home video distribution and multi-room TV",
    blurb: "One rack feeds every TV in the house — no cable box clutter in each room.",
  },
  commercial: {
    to: "/services/commercial-av",
    anchor: "commercial AV installation for Denver businesses",
    blurb: "Conference rooms, digital signage, and distributed audio for offices and venues.",
  },
  security: {
    to: "/services/security",
    anchor: "4K security cameras and smart lock installation",
    blurb: "Camera coverage, doorbells, and locks integrated into one interface.",
  },
};

/** Published blog guides with descriptive anchors, mapped to topics. */
export const guideLinks: Array<LinkItem & { slug: string; topics: TopicKey[] }> = [
  {
    slug: "custom-home-theater-design-guide",
    to: "/blog/custom-home-theater-design-guide",
    anchor: "custom home theater design guide: dimensions, sightlines, and acoustics",
    blurb: "Room ratios, screen and seating geometry, speaker layout, and acoustic treatment.",
    topics: ["home-theater", "dolby-atmos", "prewire", "whole-home-audio"],
  },
  {
    slug: "planning-a-home-theater",
    to: "/blog/planning-a-home-theater",
    anchor: "how to plan a home theater that actually matters",
    blurb: "Room, display, sound, seating, and control decisions in the order they matter.",
    topics: ["home-theater", "dolby-atmos", "prewire", "whole-home-audio"],
  },
  {
    slug: "choosing-a-smart-home-platform",
    to: "/blog/choosing-a-smart-home-platform",
    anchor: "how to choose a smart home platform",
    blurb: "What to evaluate before you commit, and why the installer outranks the brand.",
    topics: ["smart-home", "lighting", "networking"],
  },
  {
    slug: "apex-av-now-a-control4-dealer-denver",
    to: "/blog/apex-av-now-a-control4-dealer-denver",
    anchor: "Apex AV is now an authorized Control4 dealer in Denver",
    blurb: "What certified Control4 dealer status means for design, programming, and support.",
    topics: ["control4", "smart-home", "lighting", "shading", "networking"],
  },


  {
    slug: "smart-home-cost-denver-budget-guide",
    to: "/blog/smart-home-cost-denver-budget-guide",
    anchor: "what a smart home actually costs in Denver",
    blurb: "Realistic budget tiers for automation, lighting, and whole-home control.",
    topics: ["smart-home", "lighting", "shading", "security"],
  },
  {
    slug: "smart-lighting-and-shading-basics",
    to: "/blog/smart-lighting-and-shading-basics",
    anchor: "a buyer's primer on smart lighting and shading",
    blurb: "Dimming, tunable white, and motorized shade basics before you spec anything.",
    topics: ["lighting", "shading", "smart-home"],
  },
  {
    slug: "outdoor-tv-and-audio-considerations",
    to: "/blog/outdoor-tv-and-audio-considerations",
    anchor: "what to know before installing an outdoor TV and audio",
    blurb: "Brightness, weather rating, and power planning for Colorado patios.",
    topics: ["outdoor", "tv-mounting", "whole-home-audio"],
  },
  {
    slug: "home-wifi-that-actually-works",
    to: "/blog/home-wifi-that-actually-works",
    anchor: "building home Wi-Fi that actually works",
    blurb: "Why mesh alone fails in large homes and what to wire instead.",
    topics: ["networking", "smart-home", "prewire"],
  },
  {
    slug: "low-voltage-prewire-checklist",
    to: "/blog/low-voltage-prewire-checklist",
    anchor: "the low-voltage prewire checklist for new construction",
    blurb: "Every drop to plan before drywall on a new build or major remodel.",
    topics: ["prewire", "networking", "home-theater"],
  },
  {
    slug: "denver-tv-mounting-guide",
    to: "/blog/denver-tv-mounting-guide",
    anchor: "the Denver TV mounting guide to heights, studs, and hidden wires",
    blurb: "Mount height math, stud and fireplace realities, and clean wire routing.",
    topics: ["tv-mounting", "home-theater"],
  },
  {
    slug: "how-to-hide-tv-wires-denver",
    to: "/blog/how-to-hide-tv-wires-denver",
    anchor: "five clean methods to hide TV wires in Denver homes",
    blurb: "In-wall kits, recessed outlets, and retrofit fishing options compared.",
    topics: ["tv-mounting", "home-theater"],
  },
  {
    slug: "home-technology-for-professionals",
    to: "/blog/home-technology-for-professionals",
    anchor: "smart home technology integration for Denver builders and architects",
    blurb: "Coordination, code, and prewire guidance for 2026 Colorado projects.",
    topics: ["prewire", "smart-home", "networking"],
  },
];

/** Suburbs we prioritize for local internal links, in order. */
const PRIORITY_CITY_SLUGS = [
  "denver",
  "cherry-creek",
  "highlands-ranch",
  "castle-rock",
  "castle-pines",
  "parker",
  "littleton",
  "centennial",
  "boulder",
  "arvada",
  "lakewood",
  "golden",
];

const TOPIC_CITY_PHRASE: Record<TopicKey, string> = {
  "home-theater": "home theater installation in",
  "dolby-atmos": "Dolby Atmos home theater installation in",
  control4: "Control4 installation in",
  "smart-home": "smart home installation in",
  "whole-home-audio": "whole-home audio installation in",
  lighting: "lighting control installation in",
  shading: "motorized shade installation in",
  outdoor: "outdoor TV and audio installation in",
  networking: "home networking and Wi-Fi in",
  prewire: "new construction prewire in",
  "tv-mounting": "TV mounting in",
  "av-installation": "AV installation in",
  "whole-home-video": "whole-home video installation in",
  commercial: "commercial AV installation in",
  security: "security camera installation in",
};

/**
 * Suburb landing-page links with topic-specific descriptive anchors.
 * Skips `excludeSlug` so a page never links to itself.
 */
export function cityLinksForTopic(topic: TopicKey, limit = 6, excludeSlug?: string): LinkItem[] {
  const phrase = TOPIC_CITY_PHRASE[topic];
  const ordered = [
    ...PRIORITY_CITY_SLUGS.map((s) => serviceAreas.find((a) => a.slug === s)).filter(Boolean),
    ...serviceAreas.filter((a) => !PRIORITY_CITY_SLUGS.includes(a.slug)),
  ] as typeof serviceAreas;

  return ordered
    .filter((a) => a.slug !== excludeSlug)
    .slice(0, limit)
    .map((a) => ({
      to: `/service-areas/${a.slug}`,
      anchor: `${phrase} ${a.name}, CO`,
      blurb: a.blurb,
    }));
}

/** Nearby-suburb links for a given city page. */
export function nearbyCityLinks(slug: string, limit = 5): LinkItem[] {
  const NEIGHBORS: Record<string, string[]> = {
    denver: ["cherry-creek", "englewood", "lakewood", "arvada", "littleton"],
    aurora: ["centennial", "denver", "parker", "englewood", "cherry-creek"],
    lakewood: ["golden", "arvada", "denver", "littleton", "westminster"],
    centennial: ["highlands-ranch", "littleton", "englewood", "parker", "aurora"],
    "highlands-ranch": ["littleton", "castle-pines", "centennial", "parker", "castle-rock"],
    littleton: ["highlands-ranch", "centennial", "englewood", "lakewood", "denver"],
    englewood: ["cherry-creek", "denver", "littleton", "centennial", "highlands-ranch"],
    "cherry-creek": ["denver", "englewood", "centennial", "lakewood", "highlands-ranch"],
    parker: ["castle-rock", "castle-pines", "centennial", "aurora", "franktown"],
    "castle-rock": ["castle-pines", "parker", "franktown", "highlands-ranch", "littleton"],
    "castle-pines": ["castle-rock", "highlands-ranch", "parker", "littleton", "centennial"],
    franktown: ["castle-rock", "parker", "castle-pines", "centennial", "highlands-ranch"],
    thornton: ["westminster", "broomfield", "arvada", "denver", "boulder"],
    westminster: ["broomfield", "thornton", "arvada", "denver", "boulder"],
    arvada: ["westminster", "golden", "broomfield", "lakewood", "denver"],
    broomfield: ["westminster", "boulder", "thornton", "arvada", "golden"],
    boulder: ["broomfield", "westminster", "golden", "arvada", "thornton"],
    golden: ["lakewood", "arvada", "boulder", "westminster", "denver"],
  };
  const list = NEIGHBORS[slug] ?? PRIORITY_CITY_SLUGS.filter((s) => s !== slug);
  return list
    .slice(0, limit)
    .map((s) => serviceAreas.find((a) => a.slug === s))
    .filter((a): a is (typeof serviceAreas)[number] => Boolean(a))
    .map((a) => ({
      to: `/service-areas/${a.slug}`,
      anchor: `home automation and AV installation in ${a.name}, CO`,
      blurb: a.blurb,
    }));
}

/** Guides for a set of topics, excluding the current post. */
export function guidesForTopics(topics: TopicKey[], limit = 4, excludeSlug?: string): LinkItem[] {
  const scored = guideLinks
    .filter((g) => g.slug !== excludeSlug)
    .map((g) => ({ g, score: g.topics.filter((t) => topics.includes(t)).length }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(({ g }) => ({ to: g.to, anchor: g.anchor, blurb: g.blurb }));
}

/**
 * Money pages — lowest keyword difficulty per Semrush, so they get the most
 * internal link equity. Order matters: earlier = higher priority.
 */
export const MONEY_TOPICS: TopicKey[] = ["tv-mounting", "smart-home", "home-theater"];

/**
 * Service-page links for a set of topics, weighted toward the money pages.
 * Relevant money topics are surfaced first, then remaining relevant topics,
 * then any leftover slots are topped up with money pages.
 */
export function servicesForTopics(topics: TopicKey[], limit = 4, excludeTo?: string): LinkItem[] {
  const rank = (t: TopicKey) => {
    const i = MONEY_TOPICS.indexOf(t);
    return i === -1 ? MONEY_TOPICS.length : i;
  };
  const relevant = Array.from(new Set(topics)).sort((a, b) => rank(a) - rank(b));
  const ordered = [...relevant, ...MONEY_TOPICS];

  const seen = new Set<TopicKey>();
  const out: LinkItem[] = [];
  for (const t of ordered) {
    if (seen.has(t)) continue;
    seen.add(t);
    const link = serviceLinks[t];
    if (link && link.to !== excludeTo) out.push(link);
    if (out.length >= limit) break;
  }
  return out;
}


const TOPIC_MATCHERS: Array<{ topic: TopicKey; patterns: RegExp }> = [
  { topic: "home-theater", patterns: /theater|theatre|atmos|projector|surround|media room/i },
  { topic: "tv-mounting", patterns: /tv mount|mounting|hide (tv )?wires|wire|fireplace/i },
  { topic: "control4", patterns: /control4|control 4/i },
  { topic: "smart-home", patterns: /smart home|automation|control4|lutron|platform|voice/i },
  { topic: "lighting", patterns: /lighting|dimmer|keypad|tunable/i },
  { topic: "shading", patterns: /shade|shading|blind|window treatment/i },
  { topic: "outdoor", patterns: /outdoor|patio|landscape|backyard/i },
  { topic: "networking", patterns: /wi-?fi|network|router|access point|bandwidth/i },
  { topic: "prewire", patterns: /prewire|pre-wire|new construction|structured wiring|low.?voltage|builder|architect/i },
  { topic: "whole-home-audio", patterns: /whole.?home audio|multi.?room|speaker|audio/i },
  { topic: "security", patterns: /security|camera|lock|doorbell|surveillance/i },
];

/** Infer topics from a blog post's text so links stay contextual. */
export function inferTopics(input: {
  title?: string | null;
  category?: string | null;
  description?: string | null;
  keywords?: string | null;
}): TopicKey[] {
  const hay = [input.title, input.category, input.description, input.keywords].filter(Boolean).join(" ");
  const topics = TOPIC_MATCHERS.filter((m) => m.patterns.test(hay)).map((m) => m.topic);
  return topics.length > 0 ? topics : ["smart-home", "home-theater"];
}
